// **The cue sequencer** (`demo-presentation-shell` task 3.2/3.4/3.6/3.8/3.10, design **DD2**/
// **DD13**). A dumb cursor over one integer, exactly as DD2 states — every other fact the demo
// surface renders (which slide, which reveal step, which chapter) is **derived** from that one
// integer and the cue list, never tracked as separate mutable state. That is what makes reversal
// symmetric for free (task 3.5) and what makes stage reset restore the HUD "the cursor implies,
// not the accumulated chrome" (task 3.9) true by construction rather than by a second code path
// that has to be kept in sync with the first.
//
// The one thing that is **not** free is the demo *world*: it lives behind the injected
// `DemoStageDriver` (`stage-driver.ts`), and the sequencer's only responsibility toward it is
// **when** to call which method, not how. Two rules, both from design DD13:
//
//   1. Advancing past a `stage` cue **for the first time since the world was last (re)built**
//      applies its op once. Reversing past it and re-advancing into it does **not** re-apply it —
//      the same world is still live, so there is nothing to redo.
//   2. `reset()` rebuilds the world from scratch: find the nearest preceding `configure` op
//      (`configureAnchorIndex`), re-run it, then replay every `stage` cue after it up to and
//      including the cursor, in order, with no timing. The cursor never moves. `jumpToChapter`
//      is the same rebuild, aimed at a chapter's first cue instead of the current cursor.

import type { Cue, ChapterId } from './cues';
import { CUE_LIST, chapterFirstIndex, configureAnchorIndex } from './cues';
import type { DemoStageDriver } from './stage-driver';

export interface DemoSequencerView {
  readonly cursor: number;
  readonly cue: Cue;
  readonly chapter: ChapterId | undefined;
  /** The most recently shown slide's id, at or before the cursor — `null` before the first
   * `slide` cue. */
  readonly currentSlide: string | null;
  /** How many `reveal` cues have applied to `currentSlide` since it was shown, up to the cursor.
   * Reversing into a previous slide restores this to that slide's own final count, never `0`. */
  readonly revealStep: number;
}

export interface DemoSequencer extends DemoSequencerView {
  /** Space / ArrowRight. A no-op on the last cue. */
  advance(): Promise<void>;
  /** ArrowLeft. A no-op on the first cue. Stepping back off a world-mutating `stage` cue rebuilds
   * the world to the state the new cursor implies (2026-08-09 request: reversing from ten bees to
   * two must show two bees, not require a full restart), so reverse is the same rebuild `reset`
   * runs, aimed one cue earlier. Reversing across slides, `ui` cues and checkpoints stays free. */
  reverse(): Promise<void>;
  /** A number key: moves to that chapter's first cue and rebuilds the world from its configure
   * anchor through that cue. */
  jumpToChapter(chapter: ChapterId): Promise<void>;
  /** The dedicated stage-reset key: rebuilds the world the current cursor implies, without moving
   * the cursor. Idempotent — calling it twice at the same cursor leaves the same world. */
  reset(): Promise<void>;
  /** Moves the cursor to the cue carrying this `beatId`, **without rebuilding the world**. Its one
   * caller is an accepted `POST /presentation/start` landing on the live-match `nav` cue (plan
   * **D-3**), and a `nav` cue implies no stage state — the projector is over the world, not in it.
   * Inert when the running cue list has no such beat, which is what keeps a trimmed test list (or
   * a deck edited to drop the beat) from throwing mid-show. */
  jumpToBeat(beatId: string): Promise<void>;
}

function deriveSlideState(
  cueList: readonly Cue[],
  cursor: number,
): { currentSlide: string | null; revealStep: number } {
  let currentSlide: string | null = null;
  let revealStep = 0;
  for (let i = 0; i <= cursor && i < cueList.length; i++) {
    const cue = cueList[i];
    if (cue === undefined) continue;
    if (cue.kind === 'slide') {
      currentSlide = cue.slide;
      revealStep = 0;
    } else if (cue.kind === 'reveal') {
      revealStep += 1;
    }
  }
  return { currentSlide, revealStep };
}

export function createDemoSequencer(
  driver: DemoStageDriver,
  cueList: readonly Cue[] = CUE_LIST,
): DemoSequencer {
  if (cueList.length === 0) {
    throw new Error('the demo sequencer needs a non-empty cue list');
  }
  let cursor = 0;
  // Indices, within `cueList`, of `stage` cues already applied to the world's **current
  // incarnation**. Cleared whenever a `configure` op runs (a fresh incarnation begins), so a
  // reverse-then-readvance over the same territory does not repeat a mutation the live world
  // already has, while a genuine rebuild (reset/jump) starts the bookkeeping over.
  const appliedStageCues = new Set<number>();

  async function applyOp(cue: Extract<Cue, { kind: 'stage' }>): Promise<void> {
    const { op } = cue;
    switch (op.kind) {
      case 'configure':
        await driver.configure(op.stage);
        return;
      case 'spawn':
        await driver.spawn(op.spawn);
        return;
      case 'hotSwap':
        await driver.hotSwap(op.hotSwap);
        return;
      case 'fastForward':
        await driver.fastForward(op.ticks);
        return;
      case 'command':
        await driver.command(op.command);
        return;
      case 'checkpoint':
        // Its world mutation happens through live presenter/agent interaction (cues.ts's
        // header) — nothing for the sequencer to do.
        return;
    }
  }

  /** Applies cue `index`'s op if it is a fresh `stage` cue in the world's current incarnation
   * (rule 1 above). Used by forward `advance()`. */
  async function applyForward(index: number): Promise<void> {
    const cue = cueList[index];
    if (cue === undefined || cue.kind !== 'stage' || appliedStageCues.has(index)) {
      return;
    }
    if (cue.op.kind === 'configure') {
      appliedStageCues.clear();
    }
    await applyOp(cue);
    appliedStageCues.add(index);
  }

  /** Rebuilds the world from the nearest preceding `configure` anchor through `target`, in cue
   * order, applying every `stage` cue in that range exactly once (rule 2 above). Used by both
   * `jumpToChapter` and `reset` — a jump is a rebuild aimed at a chapter's first cue; a reset is
   * the same rebuild aimed at wherever the cursor already is. */
  async function rebuildThrough(target: number): Promise<void> {
    const anchor = configureAnchorIndex(cueList, target);
    if (anchor === undefined) {
      // No chapter has been entered yet (still in the opening slides) — nothing to rebuild.
      return;
    }
    const anchorCue = cueList[anchor];
    if (anchorCue === undefined || anchorCue.kind !== 'stage' || anchorCue.op.kind !== 'configure') {
      throw new Error('configureAnchorIndex returned a cue that is not a configure stage cue');
    }
    await driver.configure(anchorCue.op.stage);
    appliedStageCues.clear();
    appliedStageCues.add(anchor);
    for (let i = anchor + 1; i <= target; i++) {
      const cue = cueList[i];
      if (cue !== undefined && cue.kind === 'stage') {
        await applyOp(cue);
        appliedStageCues.add(i);
      }
    }
  }

  function view(): DemoSequencerView {
    const cue = cueList[cursor];
    if (cue === undefined) {
      throw new Error('the demo sequencer cursor walked off the cue list');
    }
    const { currentSlide, revealStep } = deriveSlideState(cueList, cursor);
    return { cursor, cue, chapter: cue.chapter, currentSlide, revealStep };
  }

  return {
    get cursor() {
      return view().cursor;
    },
    get cue() {
      return view().cue;
    },
    get chapter() {
      return view().chapter;
    },
    get currentSlide() {
      return view().currentSlide;
    },
    get revealStep() {
      return view().revealStep;
    },

    async advance(): Promise<void> {
      if (cursor >= cueList.length - 1) {
        return; // overrunning the end is inert
      }
      cursor += 1;
      await applyForward(cursor);
    },

    async reverse(): Promise<void> {
      if (cursor <= 0) {
        return; // overrunning the start is inert
      }
      const departed = cueList[cursor];
      cursor -= 1;
      // Stepping back off a cue that mutated the world un-applies it by replaying the chain from
      // the configure anchor through the new cursor (rule 2's rebuild, aimed one cue earlier).
      // A `checkpoint`'s mutation was live presenter/agent interaction — nothing to replay — and
      // every other cue kind never touched the world, so reversing across those stays free.
      if (departed !== undefined && departed.kind === 'stage' && departed.op.kind !== 'checkpoint') {
        await rebuildThrough(cursor);
      }
    },

    async jumpToChapter(chapter: ChapterId): Promise<void> {
      const target = chapterFirstIndex(cueList, chapter);
      cursor = target;
      await rebuildThrough(target);
    },

    async reset(): Promise<void> {
      await rebuildThrough(cursor);
    },

    async jumpToBeat(beatId: string): Promise<void> {
      const target = cueList.findIndex((cue) => cue.beatId === beatId);
      if (target < 0) return;
      cursor = target;
    },
  };
}
